import React, { useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Tabs } from 'antd'
import { BarChartOutlined } from '@ant-design/icons'
import OrderDetailsTab from './OrderDetailsTab'
import RevisionsTab from './RevisionsTab'
import SubmissionsTab from './SubmissionsTab'
import AppraiserRequestTab from './AppraiserRequestTab'
import PaymentsTab from './PaymentsTab'
import Evault from './Evault'
import MessagesTab from './MessagesTab'
import InspectionSubmissionsTab from './InspectionSubmissionsTab'
import LoanDetailsTab from '../loans/LoanDetailsTab'
import OrderFeeHistoryTab from './OrderFeeHistoryTab/OrderFeeHistoryTab'
import ActivityLogTab from './ActivityLogTab/ActivityLogTab'
import RankingInformationTab from './RankingInformationTab'
import TabLabel from '../../common/layout/components/TabLabel/TabLabel'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { isPdrOrStreamlinedInspectionOrder } from '../../../helpers/pdr_orders'
import {
  checkPermission,
  checkCanViewOrderConversation,
  checkCanParticipateInOrderConversation,
  utcNow,
} from '../../common/utils/helpers'
import { useApiRequest } from '../../common/hooks'
import { setUnreadCount, approveMessage, updateMessage } from '../../store/reducers/conversation/conversationSlice'
import { USER_TYPES } from '../../common/constants/userTypes'
import { selectShowRevisions, selectShowSubmissions } from '../../store/reducers/order/orderSelectors'
import { selectLegacyConversation } from '@app/store/reducers/conversation/conversationSelectors'
import Rephrase from '../../common/utils/Rephrase'
import { prepConversationMessages, getConversationTooltips } from '@helpers/conversations'

const { TabPane } = Tabs

const SingleOrderTabs = (props) => {
  const {
    currentUserID,
    orderData,
    is_amc_lender,
    is_review_appraiser,
    refreshOrder,
    activeOrderTab,
    timeline,
    timelineStatus,
    order,
    primaryOrderId,
    should_show_consumer_payment_style_field,
    loanData,
    loanUpdate,
    using_amc_lender,
    isDragOver,
    dragLeave,
    conversation,
    review_conversation,
    use_up_to_amount,
    currentUser,
    address,
    team_conversation,
    viewRov,
    downloadRov,
    amc_conversation,
    conversationStatus,
    onHide,
    newMessage,
    onFocus,
    lenderData,
    actions,
    send_borrower_appraisal,
    auto_send_borrower_appraisal,
    auto_send_borrower_appraisal_delay,
    review_appraisal_hours_start,
    review_appraisal_hours_end,
    setActiveTab,
    activeTab,
    shouldShowFeeHistoryTab,
    shouldShowActivityHistoryTab,
  } = props

  const dispatch = useDispatch()
  const apiRequest = useApiRequest()
  const { showInspectionsTab, showRankingInformationTab } = useFlags()

  const showRevisions = useSelector(selectShowRevisions)
  const showSubmissions = useSelector(selectShowSubmissions)
  const legacyConversation = useSelector(selectLegacyConversation)
  const isPDROrStreamlined = isPdrOrStreamlinedInspectionOrder(order)

  const markReadMessages = useCallback(
    async (conversationId, type) => {
      if (conversationId) {
        await apiRequest({
          endpoint: 'order/messages/read',
          method: 'PUT',
          body: { conversation_id: conversationId },
        })
        dispatch(
          setUnreadCount({
            type,
            unread_messages: 0,
          }),
        )
      }
    },
    [dispatch, apiRequest],
  )

  const handleApproveMessage = (messageRecord) => {
    let message = {
      requires_approval: false,
      approval_timestamp: utcNow(),
    }
    if (messageRecord.isApproved) {
      message = {
        ...message,
        visible_to_vendor: true,
        visible_to_internal: true,
      }
    }
    dispatch(
      updateMessage({
        conversationType: 'unified_conversation',
        messageId: messageRecord.messageId,
        message,
      }),
    )
    dispatch(approveMessage(messageRecord))
  }
  let userCanApproveMessages = false
  try {
    // checkPermission can raise error if permission missing
    userCanApproveMessages = checkPermission('approve_conversation_messages')
  } catch {
    userCanApproveMessages = false
  }

  let messages = []
  let hasConversation = false
  let hasAMCConversation = false
  let hasTeamConversation = false
  let hasAmpUnifiedConversation = false
  let hasReviewConversation = false
  let teamConversationParticipation = false
  const canSeeSubmissionTab = showSubmissions
  let amc_messages = []
  let review_messages = []
  let team_messages = []
  let messagesBubbles
  let amcMessagesBubbles
  let reviewMessagesBubbles
  let teamMessagesBubbles
  let primaryConversationType
  let primaryConversationId
  let primaryMessages
  let pastAppraiserMessages
  let primaryMessagesBubbles
  let primaryMessagesParticipation
  let primaryUnreadMessages
  let conversationParticipation
  let amcConversationParticipation
  let reviewConversationParticipation
  let appraiserConversation
  let appraiserMessages
  let appraiserMessageBubbles

  const currentUserType = is_amc_lender ? USER_TYPES.AMC : USER_TYPES.LENDER
  if (orderData) {
    if (conversation) {
      appraiserConversation = conversation
      const isPastAppraiserConversation = false
      const messagesToPrep = [
        ...conversation.messages,
        ...(orderData.current_vendor_messages || []).filter((m) => m.is_amc),
      ]
      const m = prepConversationMessages(
        messagesToPrep,
        conversation.id,
        currentUserID,
        currentUserType,
        isPastAppraiserConversation,
        orderData,
        handleApproveMessage,
        userCanApproveMessages,
      )
      messages = m.messages
      messagesBubbles = getConversationTooltips(conversation, orderData)
      appraiserMessages = messages
      appraiserMessageBubbles = messagesBubbles
      // revisions = m['revisions']
      if (orderData.job_type === 'amc') {
        if (conversation.type === 'unified_conversation') {
          hasAmpUnifiedConversation = true
          // For AMP unified conversations, use the legacy convo for communication
          // between AMP and the appraiser
          if (is_amc_lender) {
            appraiserConversation = legacyConversation
            if (appraiserConversation) {
              const availableMessages = [
                ...legacyConversation.messages,
                ...(orderData.current_vendor_messages || []).filter((m) => m.is_amc),
              ]
              const preppedAppraiserMessages = prepConversationMessages(
                availableMessages,
                legacyConversation.id,
                currentUserID,
                currentUserType,
                isPastAppraiserConversation,
                orderData,
                false,
                false,
              )
              appraiserMessages = preppedAppraiserMessages.messages
              appraiserMessageBubbles = getConversationTooltips(legacyConversation, orderData)
            }
          }
          if (checkPermission('view_order_conversation')) {
            hasConversation = true
            if (checkPermission('participate_conversation')) {
              conversationParticipation = true
            }
          }
          conversationParticipation = true
        } else {
          if (checkPermission('view_amc_appraiser_conversation')) {
            hasConversation = true
            if (checkPermission('participate_amc_appraiser_conversation')) {
              conversationParticipation = true
            }
          }
        }
      } else {
        if (checkCanViewOrderConversation(orderData)) {
          hasConversation = true

          if (checkCanParticipateInOrderConversation(orderData)) {
            conversationParticipation = true
          }
        }
      }
    }

    if (amc_conversation) {
      const isPastAppraiserConversation = false
      const amc_m = prepConversationMessages(
        amc_conversation.messages,
        amc_conversation.id,
        currentUserID,
        currentUserType,
        isPastAppraiserConversation,
      )

      amc_messages = amc_m.messages
      amcMessagesBubbles = getConversationTooltips(amc_conversation, orderData)

      if (orderData.job_type === 'amc') {
        if (checkPermission('view_order_conversation')) {
          hasAMCConversation = true
          if (checkPermission('participate_conversation')) {
            amcConversationParticipation = true
          }
        }
      }
    }

    if (review_conversation) {
      const isPastAppraiserConversation = false
      const review_m = prepConversationMessages(
        review_conversation.messages,
        review_conversation.id,
        currentUserID,
        currentUserType,
        isPastAppraiserConversation,
      )
      review_messages = review_m.messages
      reviewMessagesBubbles = getConversationTooltips(review_conversation, orderData)

      if (checkPermission('view_review_conversation')) {
        hasReviewConversation = true

        if (checkPermission('participate_review_conversation')) {
          reviewConversationParticipation = true
        }
      }
    }

    if (team_conversation) {
      const isPastAppraiserConversation = false
      const team_m = prepConversationMessages(
        team_conversation.messages,
        team_conversation.id,
        currentUserID,
        currentUserType,
        isPastAppraiserConversation,
      )

      team_messages = team_m.messages
      teamMessagesBubbles = getConversationTooltips(team_conversation, orderData)

      if (checkPermission('view_team_conversation')) {
        hasTeamConversation = true
      }

      if (checkPermission('participate_team_conversation')) {
        teamConversationParticipation = true
      }
    }

    if (orderData.past_appraiser_conversations) {
      pastAppraiserMessages = []

      orderData.past_appraiser_conversations.forEach((conversation) => {
        const isPastAppraiserConversation = true
        const past_m = prepConversationMessages(
          conversation.messages,
          conversation.id,
          currentUserID,
          currentUserType,
          isPastAppraiserConversation,
        )

        pastAppraiserMessages = [...pastAppraiserMessages, ...past_m.messages]
      })

      if (checkCanViewOrderConversation(orderData)) {
        hasConversation = true
      }
    }

    // Use this variable to change out the orderdata timeline view depending on the order type.
    if (orderData.job_type === 'amc' && hasAMCConversation && !hasAmpUnifiedConversation) {
      primaryConversationType = 'amc_conversation'
      primaryConversationId = amc_conversation?.id
      primaryMessages = amc_messages
      primaryMessagesBubbles = amcMessagesBubbles
      primaryMessagesParticipation = amcConversationParticipation
      primaryUnreadMessages = amc_conversation?.unread_messages || 0
    } else if (hasConversation) {
      primaryConversationType = conversation?.type || 'conversation'
      primaryConversationId = conversation?.id
      primaryMessages = messages
      primaryMessagesBubbles = messagesBubbles
      primaryMessagesParticipation = conversationParticipation
      primaryUnreadMessages = conversation?.unread_messages || 0
    }
  }

  useEffect(() => {
    if (activeTab === '1' && primaryConversationId && primaryUnreadMessages) {
      markReadMessages(primaryConversationId, primaryConversationType)
    }
  }, [activeTab, primaryConversationId, primaryConversationType, primaryUnreadMessages, markReadMessages])

  let lastSubmission = null
  if (orderData && orderData.submissions && orderData.submissions.length > 0) {
    orderData.submissions.forEach((submission) => {
      lastSubmission = submission
    })
  }

  const onTabChange = async (key) => {
    setActiveTab(key)

    switch (key) {
      case '3':
        markReadMessages(conversation.id, 'conversation')
        break
      case '9':
        markReadMessages(review_conversation.id, 'review_conversation')
        break
      case '12':
        markReadMessages(team_conversation.id, 'team_conversation')
        break
      default:
        break
    }
  }

  const shouldShowEvaultTab = () => {
    if (orderData) {
      if (is_amc_lender || orderData.job_type !== 'amc') {
        if (checkPermission('p_and_s_view') || (checkPermission('evault_view') && orderData.evault)) {
          return true
        }
      }
    }

    return false
  }

  const shouldShowAmcEvaultTab = () => {
    if (orderData && orderData.amc_evault) {
      if (checkPermission('evault_view') || checkPermission('p_and_s_view')) {
        return true
      }
    }

    return false
  }

  const nonAmcOrder = orderData && orderData.job_type !== 'amc'
  const notWFP = orderData && orderData.statusKey !== 'waiting_for_payment'
  const broadcastOrder = orderData && orderData.order_request_method === 'broadcast'
  const individuallyOrder = orderData && orderData.order_request_method === 'individually'
  const cascadeOrder = orderData && orderData.order_request_method === 'cascade'
  const findingAppraisers = orderData && orderData.statusKey === 'finding_appraisers'
  const cascadeOrderFindingAppraisers = cascadeOrder && findingAppraisers
  const allowCounterOffer = orderData && orderData.allow_counter_offer && findingAppraisers
  const orderHasPendingClientLenderOffer =
    orderData &&
    orderData.assignment_list &&
    orderData.assignment_list.some(
      (assignment) => assignment.offers && assignment.offers.some((offer) => offer.is_pending_client_lender),
    )

  const showAppraisalRequests =
    is_amc_lender ||
    orderHasPendingClientLenderOffer ||
    (nonAmcOrder && notWFP && (broadcastOrder || ((individuallyOrder || cascadeOrder) && allowCounterOffer)))

  const ruccCode = is_amc_lender && 'rucc_code' in loanData ? loanData.rucc_code : 0

  return (
    <Tabs defaultActiveKey={activeTab} type="card" onChange={onTabChange}>
      {orderData && (
        <TabPane tab={<TabLabel label="Order Details" iconType="profile" />} key="1">
          <OrderDetailsTab
            currentUserId={currentUserID}
            orderData={orderData}
            is_amc_lender={is_amc_lender}
            is_review_appraiser={is_review_appraiser}
            refreshOrder={refreshOrder}
            activeOrderTab={activeOrderTab}
            markReadMessages={markReadMessages}
            messages={messages}
            timelineData={timeline}
            timelineLoading={timelineStatus === 'loading'}
            order={order}
            currentUserType={currentUserType}
            primaryOrderId={primaryOrderId}
            primaryConversationType={primaryConversationType}
            primaryMessages={primaryMessages}
            pastAppraiserMessages={pastAppraiserMessages}
            primaryMessagesBubbles={primaryMessagesBubbles}
            allowMessageParticipation={primaryMessagesParticipation}
            should_show_consumer_payment_style_field={should_show_consumer_payment_style_field}
            branchData={loanData.branch}
            conversationStatus={conversationStatus}
            onHide={onHide}
            amc_conversation={amc_conversation}
            conversation={conversation}
            onFocus={onFocus}
            newMessage={newMessage}
            ruccCode={ruccCode}
          />
        </TabPane>
      )}
      <TabPane tab={<TabLabel label="Loan Details" iconType="folder" />} key="6">
        <LoanDetailsTab
          loanData={loanData}
          is_amc_lender={is_amc_lender}
          hasOrder={!!orderData}
          loanUpdate={loanUpdate}
          refreshOrder={refreshOrder}
          order_due_date={orderData && (orderData.lender_due_date || orderData.due_date)}
          should_show_consumer_payment_style_field={should_show_consumer_payment_style_field}
          consumer_payment_style={orderData ? orderData.consumer_payment_style : null}
        />
      </TabPane>
      {showAppraisalRequests && (
        <TabPane
          tab={
            <TabLabel
              label={<Rephrase sayOrder>Appraisal Requests</Rephrase>}
              iconType="solution"
              badgeCount={!orderData.accepted ? orderData.outstanding_offer_count : 0}
            />
          }
          key="13"
        >
          <AppraiserRequestTab
            orderData={orderData}
            using_amc_lender={using_amc_lender}
            currentUserID={currentUserID}
            markReadMessages={markReadMessages}
            is_amc_lender={is_amc_lender}
            lenderData={lenderData}
            order={order}
            refreshOrder={refreshOrder}
          />
        </TabPane>
      )}
      {shouldShowEvaultTab() && (
        <TabPane
          tab={
            <TabLabel
              label={
                <Rephrase>{`eVault Documents ${is_amc_lender ? `${isPDROrStreamlined ? '(Vendor)' : '(Appraiser)'}` : ''}`}</Rephrase>
              }
              iconType="safety"
            />
          }
          key="2"
        >
          <Evault
            isDragOver={isDragOver}
            dragLeave={dragLeave}
            orderData={orderData}
            type="evault"
            primaryOrderId={primaryOrderId}
            subOrderId={activeOrderTab !== primaryOrderId && activeOrderTab}
            is_review_appraiser={is_review_appraiser}
            is_amc_lender={is_amc_lender}
          />
        </TabPane>
      )}
      {orderData && checkPermission('team_documents_view') && (
        <TabPane tab={<TabLabel label="Team Documents" iconType="safety" />} key="14">
          <Evault
            isTeamDocs={true}
            isDragOver={isDragOver}
            dragLeave={dragLeave}
            orderData={orderData}
            type="team_documents"
            primaryOrderId={primaryOrderId}
            subOrderId={activeOrderTab !== primaryOrderId && activeOrderTab}
            is_review_appraiser={is_review_appraiser}
            is_amc_lender={is_amc_lender}
          />
        </TabPane>
      )}
      {shouldShowAmcEvaultTab() && (
        <TabPane
          tab={<TabLabel label={`eVault Documents ${is_amc_lender ? '(Lender)' : ''}`} iconType="safety" />}
          key="10"
        >
          <Evault
            isDragOver={isDragOver}
            dragLeave={dragLeave}
            orderData={orderData}
            type="amc_evault"
            primaryOrderId={primaryOrderId}
            subOrderId={activeOrderTab !== primaryOrderId && activeOrderTab}
            is_review_appraiser={is_review_appraiser}
            is_amc_lender={is_amc_lender}
          />
        </TabPane>
      )}
      {appraiserConversation && is_amc_lender && !cascadeOrderFindingAppraisers && (
        <TabPane
          tab={
            <TabLabel
              label={<Rephrase>Appraiser Messages</Rephrase>}
              iconType="message"
              badgeCount={appraiserConversation ? appraiserConversation.unread_messages : 0}
            />
          }
          key="3"
        >
          <MessagesTab
            messagesBubbles={appraiserMessageBubbles}
            messages={appraiserMessages}
            conversationParticipation={conversationParticipation}
            conversation={appraiserConversation}
            markReadMessages={markReadMessages}
            is_amc_lender={is_amc_lender}
            subOrderId={activeOrderTab !== primaryOrderId && activeOrderTab}
            conversationType="conversation"
            isAppraiserMessages
            newMessageTitle="Conversation"
            order={order}
            orderData={orderData}
            refreshOrder={refreshOrder}
            onHide={onHide}
          />
        </TabPane>
      )}
      {hasReviewConversation && is_amc_lender && (
        <TabPane
          tab={
            <TabLabel
              label="Review Conversation"
              iconType="message"
              badgeCount={review_conversation ? review_conversation.unread_messages : 0}
            />
          }
          key="9"
        >
          <MessagesTab
            messagesBubbles={reviewMessagesBubbles}
            messages={review_messages}
            conversationParticipation={reviewConversationParticipation}
            conversation={review_conversation}
            markReadMessages={markReadMessages}
            conversationType="review_conversation"
            newMessageTitle="Review Conversation"
            is_amc_lender={is_amc_lender}
            order={order}
            orderData={orderData}
          />
        </TabPane>
      )}
      {orderData && checkPermission('submission_view') && canSeeSubmissionTab && (
        <TabPane tab={<TabLabel label="Submissions" iconType="inbox" />} key="4">
          <SubmissionsTab
            orderData={orderData}
            loanData={loanData}
            is_amc_lender={is_amc_lender}
            lenderData={lenderData}
            actions={actions}
            send_borrower_appraisal={send_borrower_appraisal}
            auto_send_borrower_appraisal={auto_send_borrower_appraisal}
            auto_send_borrower_appraisal_delay={auto_send_borrower_appraisal_delay}
            review_appraisal_hours_start={review_appraisal_hours_start}
            review_appraisal_hours_end={review_appraisal_hours_end}
            order={order}
            currentUserId={currentUserID}
            refreshOrder={refreshOrder}
            is_review_appraiser={is_review_appraiser}
            onHide={onHide}
            canSeeSubmissionTab={canSeeSubmissionTab}
          />
        </TabPane>
      )}
      {showRevisions && (
        <TabPane tab={<TabLabel label="Revisions" iconType="schedule" />} key="5">
          <RevisionsTab
            orderData={orderData}
            is_amc_lender={is_amc_lender}
            refreshOrder={refreshOrder}
            viewRov={viewRov}
            downloadRov={downloadRov}
            lastSubmission={lastSubmission}
            useOldROV={currentUser?.lender?.use_old_rov}
          />
        </TabPane>
      )}
      {orderData && orderData.job_type !== 'review' && checkPermission('payments_view') && (
        <TabPane tab={<TabLabel label="Payment" iconType="schedule" />} key="11">
          <PaymentsTab
            orderData={orderData}
            order={order}
            currentUserID={currentUserID}
            loanData={loanData}
            refreshOrder={refreshOrder}
            use_up_to_amount={use_up_to_amount}
            currentUser={currentUser}
            address={address}
            is_amc_lender={is_amc_lender}
            lenderData={lenderData}
          />
        </TabPane>
      )}
      {hasTeamConversation && primaryConversationType !== 'unified_conversation' && (
        <TabPane
          tab={
            <TabLabel
              label="Team Conversation"
              iconType="message"
              badgeCount={team_conversation ? team_conversation.unread_messages : 0}
            />
          }
          key="12"
        >
          <MessagesTab
            messagesBubbles={teamMessagesBubbles}
            messages={team_messages}
            conversationParticipation={teamConversationParticipation}
            conversation={team_conversation}
            conversationType="team_conversation"
            markReadMessages={markReadMessages}
            subOrderId={activeOrderTab !== primaryOrderId && activeOrderTab}
            newMessageTitle="Team Conversation"
            is_amc_lender={is_amc_lender}
            order={order}
            orderData={orderData}
          />
        </TabPane>
      )}
      {isPDROrStreamlined && showInspectionsTab && lastSubmission && (
        <TabPane tab={<TabLabel label="Inspections" iconType="inbox" />} key="16">
          <InspectionSubmissionsTab
            order={order}
            orderData={orderData}
            is_amc_lender={is_amc_lender}
            refreshOrder={refreshOrder}
          />
        </TabPane>
      )}
      {shouldShowFeeHistoryTab && (
        <TabPane tab={<TabLabel label="Fee History" iconType="dollar" />} key="17">
          <OrderFeeHistoryTab orderFeeChanges={order.order_fee_changes} />
        </TabPane>
      )}
      {shouldShowActivityHistoryTab && (
        <TabPane tab={<TabLabel label="Activity Log" iconType="history" />} key="18">
          <ActivityLogTab orderId={order.id} />
        </TabPane>
      )}
      {showRankingInformationTab && (
        <TabPane
          tab={
            <>
              <BarChartOutlined /> Ranking Information
            </>
          }
          key="19"
        >
          <RankingInformationTab />
        </TabPane>
      )}
    </Tabs>
  )
}

export default SingleOrderTabs
